import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isRafaelUser } from '@/contexts/auth/permissions';
import { normalizarDocumento } from '@/lib/fiscal/sped/documentos';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- tabelas fora dos tipos gerados do Supabase, mesmo padrão do resto do projeto
const sb: any = supabase;

export type ClienteVinculavel = { cnpj: string; nome: string };

export const QUERY_KEY_CLIENTES_VINCULAVEIS = ['usuario-clientes-vinculaveis'] as const;
export const queryKeyClientesDoUsuario = (usuarioId: number) => ['usuario-clientes', usuarioId] as const;

/**
 * Clientes da tela Tributação › Tokens API: os do cadastro fiscal_cliente mais os CNPJs que só existem nos tokens.
 * O nome é o do cadastro; sem cadastro, o do token mais recente do CNPJ.
 */
export const useClientesVinculaveis = () =>
  useQuery({
    queryKey: QUERY_KEY_CLIENTES_VINCULAVEIS,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ClienteVinculavel[]> => {
      const [cadastro, tokens] = await Promise.all([
        sb.from('fiscal_cliente').select('cnpj_cpf, nome'),
        supabase
          .from('tributos_api_tokens')
          .select('nome, cliente_cnpj_cpf, created_at')
          .not('cliente_cnpj_cpf', 'is', null)
          .order('created_at', { ascending: false }),
      ]);
      if (tokens.error) throw tokens.error;
      const porCnpj = new Map<string, ClienteVinculavel>();
      // Sem a tabela fiscal_cliente (migration não aplicada), fica só com os tokens.
      for (const c of (cadastro.error ? [] : cadastro.data || []) as { cnpj_cpf: string; nome: string }[]) {
        porCnpj.set(c.cnpj_cpf, { cnpj: c.cnpj_cpf, nome: c.nome });
      }
      for (const t of tokens.data || []) {
        const cnpj = normalizarDocumento(t.cliente_cnpj_cpf as string | null);
        if (cnpj && !porCnpj.has(cnpj)) porCnpj.set(cnpj, { cnpj, nome: (t.nome as string) || cnpj });
      }
      return [...porCnpj.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    },
  });

/** CNPJs/CPFs vinculados ao usuário (usuario_cliente). */
export const useClientesDoUsuario = (usuarioId: number) =>
  useQuery({
    queryKey: queryKeyClientesDoUsuario(usuarioId),
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await sb.from('usuario_cliente').select('cnpj_cpf').eq('usuario_id', usuarioId);
      if (error) throw error;
      return ((data || []) as { cnpj_cpf: string }[]).map((linha) => linha.cnpj_cpf);
    },
  });

/** Grava exatamente estes clientes para o usuário: inclui os novos e tira os desmarcados. */
export async function salvarClientesDoUsuario(usuarioId: number, cnpjs: string[], atuais: string[], criadoPor: string): Promise<void> {
  const tirar = atuais.filter((c) => !cnpjs.includes(c));
  const incluir = cnpjs.filter((c) => !atuais.includes(c));
  if (tirar.length) {
    const { error } = await sb.from('usuario_cliente').delete().eq('usuario_id', usuarioId).in('cnpj_cpf', tirar);
    if (error) throw new Error(error.message);
  }
  if (incluir.length) {
    const { error } = await sb
      .from('usuario_cliente')
      .insert(incluir.map((cnpj_cpf) => ({ usuario_id: usuarioId, cnpj_cpf, criado_por: criadoPor })));
    if (error) throw new Error(error.message);
  }
}

export type ClientesPermitidos = {
  /** Admin e Consultor (e o Rafael): todos os clientes. */
  todos: boolean;
  /** CNPJs/CPFs do Restrito; vazio enquanto carrega. */
  cnpjs: ReadonlySet<string>;
  pode: (cnpj: string | null | undefined) => boolean;
};

/**
 * Quais clientes o usuário logado vê: Admin e Consultor veem todos; Restrito, só os vinculados em usuario_cliente.
 * O filtro é nas telas (lista de clientes, Painel fiscal, Tokens API), não no banco.
 */
export function useClientesPermitidos(): ClientesPermitidos {
  const { user } = useAuth();
  const perfil = user?.perfil_padrao?.toLowerCase();
  const todos = !!user && (perfil === 'admin' || perfil === 'consultor' || isRafaelUser(user));
  const { data } = useQuery({
    queryKey: queryKeyClientesDoUsuario(user?.id ?? 0),
    queryFn: async (): Promise<string[]> => {
      const { data: linhas, error } = await sb.from('usuario_cliente').select('cnpj_cpf').eq('usuario_id', user?.id);
      if (error) throw error;
      return ((linhas || []) as { cnpj_cpf: string }[]).map((linha) => linha.cnpj_cpf);
    },
    enabled: !!user && !todos,
    staleTime: 5 * 60 * 1000,
  });
  return useMemo(() => {
    const cnpjs = new Set(data ?? []);
    return { todos, cnpjs, pode: (cnpj) => todos || (!!cnpj && cnpjs.has(normalizarDocumento(cnpj))) };
  }, [todos, data]);
}
